import { Alert, Badge, Button, Card, ChatMessageView, Checkbox, DataTable, DateDisplay, Heading, Icon, Loading, Sheet, SheetContent, Tabs } from "@aidkitorg/component-library";
import { ChatMessage } from "@aidkitorg/component-library/dist/types/src/ChatMessageView";
import { AIConversation } from "@aidkitorg/types/lib/convoai/conversation";
import { ColumnDef } from "@tanstack/react-table";
import { useContext, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { usePost } from "../API";
import { ThreeColumnPage } from "../Components/ThreeColumnPage";
import { PublicConfigurationContext } from "../Context";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { useLocalizedStrings } from "../Localization";
import { nonHookMarkdown } from "../Util";
import { formatDuration } from "../utils/time";
import { DEFAULT_AGENT_NAME, DEFAULT_AVATAR_URL } from "./AICoachUI";

export function AIConversationsExplorerPage(props: any) { 
  return <ThreeColumnPage title="Support"
    main={<AIConversationsExplorer {...props} />} />
}

type ConversationRow = {
  id: string,
  created_at: Date,
  created_by?: string,
  duration: number,
  from_url?: string | null,
  tokens_total: number,
  feedback_positive: number,
  feedback_negative: number,
  feedback_reason: string | null,
  transcript: string | null,
  legal_name: string | null,
  unsubmitted: boolean,
  reviewed_by_me: boolean
}
export function AIConversationsExplorer() { 
  const [loading, setLoading] = useState<boolean>(false);
  const getConversations = usePost('/coach/conversation/explore');
  const [data, setData] = useState<ConversationRow[]>([]);
  const history = useHistory();
  const { pathname } = useLocation();
  const { conversationId } = useParams < { conversationId?: string } >();
  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    setLoading(true);    
    const response = await getConversations({
      limit: 1000,
      offset: 0
    });
    if (!('error' in response) && response.results) {
      setData(response.results.map(
        r => ({
          ...r,
          created_at: new Date(r.created_at),
          feedback_positive: Number(r.feedback_positive),
          feedback_negative: Number(r.feedback_negative)
        })
      ));
    }
    setLoading(false);
  }, []);
  
  const columns : ColumnDef<ConversationRow>[] = [
    {
      accessorKey: "legal_name",
      header: L.ai_coach.dashboard.user,
      cell: ({ row }) => { 
        const { legal_name, unsubmitted, created_by } = row?.original;
        if (!created_by) { 
          return <>{L.ai_coach.dashboard.unknown_user}</>;
        }
        return <Button 
          href={`/${unsubmitted ? 'ua' : 'a'}/${created_by}`}
          variant="link"
          openInNewWindow={true}
          size="sm">
          {legal_name ?? L.ai_coach.dashboard.unknown_user}
        </Button>
      }
    },
    {
      accessorKey: "created_at",
      header: L.ai_coach.dashboard.created_at,
      cell: ({row}) => { 
        return <DateDisplay 
          utcValue={row?.original?.created_at}
          className="text-sm"
          optionOverrides={{ dateStyle: 'long', timeStyle: 'short', timeZoneName: undefined }}
          displayInUTC={false} />
      }
    },    
    {
      accessorKey: "duration",
      header: L.ai_coach.dashboard.duration,
      cell: ({ row }) => { 
        const { duration } = row?.original;
        return formatDuration(duration, 'milliseconds');
      }
    },
    {
      accessorKey: "from_url",
      header: L.ai_coach.dashboard.url,
    },
    {
      accessorKey: "feedback_positive",
      header: "👍",
    },
    {
      accessorKey: "feedback_negative",
      header: "👎",
    },
    {
      accessorKey: "feedback_reason",
      header: L.ai_coach.dashboard.feedback,
      cell: ({ row }) => { 
        const { feedback_reason } = row?.original;
        if (feedback_reason?.length) { 
          return <div className="flex flex-col gap-2 text-sm">
            {feedback_reason.split(',').map(
              (reason, i) => <div className="leading-none" key={i}>{reason}</div>
            )}            
          </div>
        }
        return null;
      }
    },
    {
      accessorKey: "tokens_total",
      header: L.ai_coach.dashboard.token_count,
      cell: ({row}) => { 
        return row?.original?.tokens_total ?? 0;
      }
    },
    {
      accessorKey: "transcript",
      header: L.ai_coach.dashboard.details,
      cell: ({ row }) => {         
        return <Button size="sm" onClick={ 
          () => history.push(`/ai_conversations/${row?.original?.id}`)
        }>{ L.ai_coach.dashboard.view_details }</Button>        
      }
    },
  ]
  
  return <Sheet 
    open={!!conversationId}
    onOpenChange={ 
      (open) => { 
        if (!open && conversationId) { 
          history.replace(pathname.replace(new RegExp(`/${conversationId}$`), ''));
        }
      }
    }>
    <div className="py-8 px-4">
      <Heading level="h2">{ L.ai_coach.dashboard.ai_conversations }</Heading>
      <DataTable
        pageSize={10}
        resizableColumns={true}
        enableSearch={true}
        columns={columns}
        data={data}
        initializing={loading}
      />
    </div>
    {conversationId && <SheetContent title={L.ai_coach.dashboard.convo_details} side="right" className="w-screen max-w-screen sm:max-w-[80vw] sm:w-xl overflow-auto">
      <AIConversationDetail id={conversationId} />
    </SheetContent>}
  </Sheet>
}

function AIConversationDetail(props: { id: string }) { 
  const getConversationById = usePost('/coach/conversation/read');
  const [loading, setLoading] = useState<boolean>(false);
  const [conversation, setConversation] = useState<(AIConversation & { created_at: Date, updated_at: Date }) | null>(null);
  const L = useLocalizedStrings();
  
  useAsyncEffect(async () => {
    setLoading(true);
    const response = await getConversationById({ id: props.id });
    if (response && !('error' in response) && response.id) {
      setConversation({
        ...response,
        created_at: new Date(response.created_at),
        updated_at: new Date(response.updated_at)
      });
    }
    setLoading(false);
  }, []);
  
  
  if (loading) { 
    return <Loading />;
  }
  
  if (!conversation) {
    return <Alert variant="danger">{ L.ai_coach.dashboard.convo_not_found }</Alert>
  }
    
  return <>
    <Heading level="h2">{ L.ai_coach.dashboard.convo_details }</Heading>
    <Tabs
      tabs={[
        {
          key: "transcript",
          name: L.ai_coach.dashboard.transcript,
          content: <TranscriptContent conversation={conversation} />
        },
        {
          key: "instructions",
          name: L.ai_coach.dashboard.instructions,
          content: <InstructionsContent conversation={conversation} />
        },
      ]}
    />
  </>
}

function TranscriptContent(props: { conversation?: AIConversation }) {   
  const { conversation } = props;
  const config = useContext(PublicConfigurationContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || DEFAULT_AVATAR_URL;
  const [onlyShowMessagesWithFeedback, setOnlyShowMessagesWithFeedback] = useState<boolean>(false);
  const L = useLocalizedStrings();
  
  const messageBubbles = useMemo(() => {
    const messages: ChatMessage[] = [];
    for (const item of conversation?.items ?? []) {
      if (item.type === "message" && (!onlyShowMessagesWithFeedback || item.meta?.feedback)) {
        messages.push({
          role: item.role,
          content: <div className="coach-chat-content overflow-x-hidden flex flex-col gap-2 divide-y divide-solid divide-indigo-200">
            <div>{nonHookMarkdown(item.content.map(c => c.content).join('\n'))}</div>
            {item.meta?.feedback && <div className="font-normal px-3 pt-2 -mx-4 gap-2">
              {item.meta?.feedback?.rating === "up" && <Icon size="md" name="HandThumbUpIcon" type="solid" />}
              {item.meta?.feedback?.rating === "down" && <div className="flex gap-2 items-center divide-solid divide-y">
                <Icon size="md" name="HandThumbDownIcon" type="solid" />
                <span>{ item.meta?.feedback?.reason ?? "No reason given" }</span>
                {item.meta?.feedback?.additionalFeedback && <span>"{ item.meta?.feedback?.additionalFeedback }"</span> }
              </div>}
            </div>}
          </div>,
          meta: { 
            time: Date.now(),
            ...(item.meta ?? {})
          },
        });
      }
    }
    return messages;
  }, [conversation?.items, onlyShowMessagesWithFeedback]);
  
  if (!conversation?.items?.length) {
    return <>{L.ai_coach.dashboard.no_transcript}</>;
  }
  
  return <div className="flex flex-col gap-4 py-4">
    <Checkbox
      label={L.ai_coach.dashboard.only_messages_with_feedback}
      checked={onlyShowMessagesWithFeedback}
      onChange={(e) => setOnlyShowMessagesWithFeedback(!!e) }
    />
    <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
        },
        user: {
          side: 'right',
          icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">U</div>,
          color: 'bg-gray-100',
        }
      }}
    />    
  </div>
}

function InstructionsContent(props: { conversation?: AIConversation }) { 
  const { conversation } = props;
  const L = useLocalizedStrings();
  let model: string | undefined;
  for (const item of conversation?.items ?? []) {
    model = item?.meta?.responseMeta?.modelMeta?.model;
    if (model) { 
      break;
    }
  }
  
  return <div className="flex flex-col gap-4 py-4">
    <div className="flex gap-2 items-center">
      <span className="font-bold">{L.ai_coach.dashboard.model}:</span>
      <Badge variant="info">{model ?? "Unknown"}</Badge>
    </div> 
    {/* TODO - hook this up to MessageTemplates */}
    {/* <div>
      <div className="font-bold">{L.ai_coach.dashboard.instructions}:</div>
      <Card>
        { nonHookMarkdown(conversation?.metadata?.instructions) }
      </Card>
    </div> */}
  </div>
}
